<template>
  <div>
    <div class="modal-wrapper"
         :class="[{'no-print': !isPrintable}, {'modal-wrapper--printable': isPrintable}, {'js-no-close': appendToBody}, {'modal-wrapper--from-top': placeInTop}]"
         ref="d_modal"
         :style="zIndex ? { zIndex } : null"
         @click.self="$emit('close')">
      <div class="modal" ref="modalContent" tabindex="-1"
           role="dialog" aria-modal="true" :aria-label="ariaLabel || null"
           :style="{ top: !placeInTop ? '0px' : pxFromTop + 'rem' }">
        <button type="button" class="close-cross" v-if="showClose" @click="$emit('close')"
                :aria-label="$t('vue2.a11y.close')">
          <span class="icon-cross2" aria-hidden="true"></span>
        </button>
        <slot name="insertComponent">
          <h2>Hello</h2>
        </slot>
      </div>
      <div class="bg-overlay animateFast fadeIn" v-if="isBgOverlay" @click.stop.self="onBgClick"></div>
    </div>
  </div>
</template>

<script>
// Delt stak over åbne modaler, så Esc kun lukker den øverste (og ikke modaler nedenunder).
const modalStack = []

// Antal åbne printbare modaler. Mens mindst én er åben, markeres <body>, så
// print-isolering (utilites.css) kun viser modalens indhold ved print.
let printableModalCount = 0

export default {
  props: {
    showClose: {
      type: Boolean,
      default: true
    },
    isBgOverlay: {
      type: Boolean,
      default: false
    },
    appendToBody: {
      type: Boolean,
      default: false
    },
    placeInTop: {
      type: Boolean,
      default: false
    },
    pxFromTop: {
      type: Number,
      default: 2
    },
    isCloseOnEsc: {
      type: Boolean,
      default: true
    },
    isCloseOnBgClick: {
      type: Boolean,
      default: true
    },
    // Tilgængeligt navn på dialogen (aria-label) for skærmlæsere.
    ariaLabel: {
      type: String,
      default: ''
    },
    // Gør modalens indhold printbart. Når true, fjernes 'no-print', og
    // print-isolering (se utilites.css) sikrer, at kun modalen printes.
    isPrintable: {
      type: Boolean,
      default: false
    },
    // Når false, flyttes fokus kun til dialogen (ikke første input),
    // så fx DSelect ikke åbner dropdown automatisk.
    autoFocusField: {
      type: Boolean,
      default: true
    },
    // Sættes på selve wrapperen, da den flyttes ud af komponentens rod ved appendToBody.
    zIndex: {
      type: Number,
      default: null
    }
  },
  data () {
    return {}
  },
  methods: {
    onBgClick () {
      if (this.isCloseOnBgClick) {
        this.$emit('close')
      }
    },
    // Synlige, fokuserbare elementer inde i dialogen (til fokus-trap).
    getFocusable () {
      const root = this.$refs.modalContent
      if (!root) return []
      const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      return Array.from(root.querySelectorAll(selector)).filter(el => {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
      })
    },
    // Sætter startfokus ved åbning. Respekterer fokus, hvis indholdet selv har sat
    // det (fx et inputfelt), og foretrækker ellers det første formularfelt, så
    // modaler med inputfelter starter dér frem for på lukkekrydset.
    focusInitial () {
      const root = this.$refs.modalContent
      if (!root) return
      if (root.contains(document.activeElement) && document.activeElement !== root) {
        return
      }
      if (!this.autoFocusField) {
        root.focus()
        return
      }
      const focusable = this.getFocusable()
      const field = focusable.find(el => /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))
      const target = field || focusable[0] || root
      target.focus()
    },
    handleKeydown (event) {
      // Kun den øverste modal må reagere, så fx Esc ikke også lukker modalen nedenunder.
      if (modalStack[modalStack.length - 1] !== this) return
      if (event.key === 'Escape') {
        if (this.isCloseOnEsc) {
          this.$emit('close')
        }
        return
      }
      // Fokus-trap: hold Tab-fokus inden for dialogen (WCAG 2.4.3).
      if (event.key === 'Tab') {
        const focusable = this.getFocusable()
        const root = this.$refs.modalContent
        if (!root) return
        if (focusable.length === 0) {
          event.preventDefault()
          root.focus()
          return
        }
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (!root.contains(document.activeElement)) {
          event.preventDefault()
          first.focus()
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
  },
  mounted () {
    if (this.appendToBody) {
      const el = this.$refs.d_modal
      const container = document.getElementById('djs-content-container')

      if (el && container) {
        container.appendChild(el)
      }
    }
    modalStack.push(this)
    if (this.isPrintable) {
      printableModalCount++
      document.body.classList.add('is-printing-modal')
    }
    window.addEventListener('keydown', this.handleKeydown)
    // Flyt fokus ind i dialogen ved åbning.
    this.$nextTick(() => {
      this.focusInitial()
    })
  },
  beforeDestroy () {
    const el = this.$refs.d_modal
    if (el && el.parentNode) {
      el.parentNode.removeChild(el)
    }
    const idx = modalStack.indexOf(this)
    if (idx > -1) {
      modalStack.splice(idx, 1)
    }
    if (this.isPrintable) {
      printableModalCount = Math.max(0, printableModalCount - 1)
      if (printableModalCount === 0) {
        document.body.classList.remove('is-printing-modal')
      }
    }
    window.removeEventListener('keydown', this.handleKeydown)
  }
}
</script>

<style scoped>
.modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal {
  position: relative;
  z-index: 9999;
  background: #f6f9fc;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .33);
  animation-name: fadeUp;
  animation-duration: 0.25s;
}

.modal-wrapper--from-top .modal {
  position: absolute;
  top: 2rem;
}

.modal__close {
  cursor: pointer;
  color: #ffffff;
}

.close-cross {
  position: absolute;
  color: #ffffff;
  top: -1.5rem;
  right: 0rem;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  background: none;
  border: none;
  padding: 0;
}

.close-cross:focus-visible,
.modal:focus-visible {
  outline: 2px solid #468AD7;
  outline-offset: 2px;
}

.d-modal__header {
  background: #fff;
  padding: 1rem;
  overflow: hidden;
  border-radius: 4px 4px 0 0;
  color: #3d4340;
  border-bottom: 1px solid #d8d8d8;
  text-align: center;
  position: relative;
}

.d-modal__header__close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 1;
  font-size: 1.2rem;
  opacity: 0.85;
}

.d-modal__body {
  width: 400px;
  padding: 1rem 2rem;
}
</style>



// WEBPACK FOOTER //
// DModal.vue?02de3ff7